import { useLogin } from '@application/api';
import type { SocialLoginMethod } from '@application/types';
import { useModal } from '@application/utils';
import { UseSocialLoginModal } from '@presentation/components';
import type { TokenResponse } from '@react-oauth/google';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useState } from 'react';
import type { ReactFacebookLoginProps } from 'react-facebook-login';
import FacebookLogin from 'react-facebook-login/dist/facebook-login-render-props';
import { singletonHook } from 'react-singleton-hook';
import { useGoogleLogin } from '@react-oauth/google';
import { ENV, environmentVariable } from '@application/env';

const init: ReturnType<typeof useSocialLoginModalSingleton> = {
  loginMethod: undefined,
  modalHook: {
    show: () => undefined,
    showFromClick: () => undefined,
    close: () => undefined,
    modal: {
      onCloseClick: undefined,
      open: false,
      position: [0, 0],
    },
  },
};

let globalLoginMethod: Dispatch<SetStateAction<SocialLoginMethod | undefined>>;

export const useSocialLoginModalSingleton = () => {
  const modalHook = useModal({});
  const [loginMethod, setGlobalLoginMethod] = useState<SocialLoginMethod>();
  globalLoginMethod = setGlobalLoginMethod;

  return {
    modalHook,
    loginMethod,
  };
};

export const setLoginMethod = (loginMethod: SocialLoginMethod) => globalLoginMethod(loginMethod);

export const useSocialLoginModal = singletonHook(init, useSocialLoginModalSingleton);

export const UseSocialLoginlModalController = () => {
  const { modalHook, loginMethod } = useSocialLoginModal();
  const { loginWithFacebookToken, loginWithGoogleToken, loginLoading } = useLogin();

  const googleAuthHandler = useCallback(
    (response: TokenResponse) => {
      loginWithGoogleToken(response.access_token);
      modalHook?.close();
    },
    [loginWithGoogleToken, modalHook],
  );

  const facebookAuthHandler: ReactFacebookLoginProps['callback'] = useCallback(
    (response) => {
      const accessToken = (response as any).accessToken as string | undefined;
      loginWithFacebookToken(accessToken);
      modalHook?.close();
    },
    [loginWithFacebookToken, modalHook],
  );
  const onGoogleSignIn = useGoogleLogin({
    onSuccess: googleAuthHandler,
  });

  return (
    <FacebookLogin
      version="4.0"
      appId={environmentVariable(ENV.FACEBOOK_ID)}
      callback={facebookAuthHandler}
      render={(renderProps: ReactFacebookLoginProps) => (
        <UseSocialLoginModal
          modal={modalHook?.modal}
          method={loginMethod}
          onGoogleClick={() => {
            onGoogleSignIn();
          }}
          onFacebookClick={renderProps.onClick}
          loading={loginLoading}
        />
      )}
    />
  );
};
